import React, { useState } from 'react';
import { AlertTriangle, Clock, Package, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useLoanStatistics, useLoans, useOverdueLoans, useActiveLoans, useInventoryStatistics, useUsers } from '@/hooks';

const AMBER = '#F59E0B';
const GRAY = '#D1D5DB';
const DONUT_COLORS = [AMBER, '#9CA3AF', '#6B7280', '#4B5563', '#374151'];

const CHART_STYLE = {
  background: '#FFFFFF',
  border: '1px solid #E8E4DC',
  borderRadius: 8,
  fontSize: 12,
  color: '#6B7280',
};

const OverviewModule: React.FC = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<'7D' | '30D' | '90D'>('30D');

  // Live data hooks
  const { data: loanStats } = useLoanStatistics();
  const { data: loansPage } = useLoans();
  const recentLoans = (loansPage as any)?.results?.slice(0, 5) ?? [];
  const { data: activeLoansData } = useActiveLoans();
  const { data: overdueLoansData } = useOverdueLoans();
  const { data: inventoryStats } = useInventoryStatistics();
  const { data: usersPage } = useUsers({ page: 1, page_size: 1 });

  const totalBooks = (inventoryStats as any)?.overview?.total_copies ?? 0;
  const activeLoans = (loanStats as any)?.total_active_loans ?? (activeLoansData as any)?.count ?? 0;
  const overdueCount = (loanStats as any)?.total_overdue_loans ?? (overdueLoansData as any)?.count ?? 0;
  const totalUsers = (usersPage as any)?.count ?? 0;

  // Build chart data (placeholder until real API data flows in)
  const chartData = Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (29 - i));
    return {
      date: d.toLocaleDateString('en', { month: 'short', day: 'numeric' }),
      loans: Math.floor(Math.random() * 12 + 2),
      returns: Math.floor(Math.random() * 10 + 1),
    };
  });

  const donutData = [
    { name: 'Fiction', value: 35 },
    { name: 'Science', value: 25 },
    { name: 'History', value: 20 },
    { name: 'Arts', value: 12 },
    { name: 'Other', value: 8 },
  ];

  const topBooks = [
    { title: 'To Kill a Mockingbird', author: 'Harper Lee', count: 24 },
    { title: '1984', author: 'George Orwell', count: 21 },
    { title: 'The Great Gatsby', author: 'F. Scott Fitzgerald', count: 18 },
    { title: 'Pride and Prejudice', author: 'Jane Austen', count: 15 },
    { title: 'The Catcher in the Rye', author: 'J.D. Salinger', count: 12 },
  ];
  const maxTopCount = topBooks[0]?.count ?? 1;

  const kpis = [
    { label: 'Total Books', value: totalBooks.toLocaleString(), trend: '+2%', up: true },
    { label: 'Active Loans', value: activeLoans.toLocaleString(), trend: '+8%', up: true },
    { label: 'Members', value: totalUsers.toLocaleString(), trend: '+5%', up: true },
    { label: 'Overdue', value: overdueCount.toLocaleString(), trend: '-3%', up: false },
  ];

  const attentionItems = [
    { label: 'Overdue', count: overdueCount, icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50', href: '/admin/loans?status=overdue' },
    { label: 'Due Today', count: 0, icon: Clock, color: 'text-amber-500', bg: 'bg-amber-50', href: '/admin/loans?status=due_today' },
    { label: 'Low Stock', count: 0, icon: Package, color: 'text-gray-500', bg: 'bg-gray-100', href: '/admin/inventory?status=low' },
  ];

  return (
    <div className="space-y-5 max-w-[1400px]">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Overview</h1>
        <p className="text-sm text-gray-500 mt-1">What's happening in your library today.</p>
      </div>

      {/* KPI strip */}
      <div className="bg-white border border-[#E8E4DC] rounded-xl">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#E8E4DC]">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="px-6 py-4">
              <div className="text-3xl font-bold text-gray-900">{kpi.value}</div>
              <div className="text-xs font-mono uppercase tracking-wider text-gray-400 mt-1">{kpi.label}</div>
              <div className={`text-xs font-medium mt-1 ${kpi.up ? 'text-green-600' : 'text-red-500'}`}>
                {kpi.up ? '▲' : '▼'} {kpi.trend}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Operational strip */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Today's Activity */}
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-5">
          <div className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-4">
            Today · {recentLoans.length} loans
          </div>
          {recentLoans.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">All quiet. No activity today.</p>
          ) : (
            <div className="space-y-0">
              {recentLoans.map((loan: any, i: number) => (
                <div key={loan.id ?? i} className="flex items-start gap-3 py-2.5 border-b border-[#F3F4F6] last:border-b-0">
                  <div className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0 mt-2" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {loan.book_copy_info?.book_title ?? '—'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {loan.borrower_info?.full_name ?? '—'} · {loan.status}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 font-mono flex-shrink-0">
                    {loan.loan_date
                      ? new Date(loan.loan_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Needs Attention */}
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-5">
          <div className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-4">
            Needs Attention
          </div>
          <div className="space-y-1">
            {attentionItems.map(({ label, count, icon: Icon, color, bg, href }) => (
              <button
                key={label}
                onClick={() => navigate(href)}
                className="flex items-center gap-3 w-full px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors text-left group border-b border-[#F3F4F6] last:border-b-0"
              >
                <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`h-4 w-4 ${color}`} />
                </div>
                <span className="text-sm font-medium text-gray-700 flex-1">{label}</span>
                {count > 0 ? (
                  <span className="bg-amber-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                    {count}
                  </span>
                ) : (
                  <span className="text-xs text-green-600 font-medium">All clear</span>
                )}
                <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Analytics strip */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Loan activity chart */}
        <div className="lg:col-span-1 bg-white border border-[#E8E4DC] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-mono uppercase tracking-wider text-gray-400">Loan Activity</div>
            <div className="flex gap-1">
              {(['7D', '30D', '90D'] as const).map(r => (
                <button
                  key={r}
                  onClick={() => setDateRange(r)}
                  className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                    dateRange === r ? 'bg-amber-500 text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DC" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9CA3AF' }} tickLine={false} interval={6} />
              <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={CHART_STYLE} />
              <Area type="monotone" dataKey="loans" stroke={AMBER} fill="#FEF3C7" strokeWidth={2} name="Loans" />
              <Area type="monotone" dataKey="returns" stroke={GRAY} fill="#F9FAFB" strokeWidth={2} name="Returns" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Category donut */}
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-5">
          <div className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-4">By Category</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={donutData} cx="50%" cy="45%" innerRadius={55} outerRadius={80} dataKey="value">
                {donutData.map((_, i) => (
                  <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                ))}
              </Pie>
              <Legend iconSize={10} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              <Tooltip contentStyle={CHART_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top books */}
        <div className="bg-white border border-[#E8E4DC] rounded-xl p-5">
          <div className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-4">Top This Month</div>
          <div className="space-y-3">
            {topBooks.map((book, i) => (
              <div key={book.title} className="flex items-start gap-2.5">
                <span className="text-xs font-mono text-amber-500 font-bold w-4 flex-shrink-0 mt-0.5">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{book.title}</div>
                  <div className="text-xs text-gray-400">{book.author}</div>
                  <div className="h-1 bg-gray-100 rounded-full mt-1.5">
                    <div
                      className="h-1 bg-amber-400 rounded-full"
                      style={{ width: `${(book.count / maxTopCount) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="text-xs text-gray-400 font-mono flex-shrink-0">{book.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewModule;
